/**
 * VoiceInput — Compact mic button for the AI Assistant input bar
 *
 * Behaviour:
 *  - Click to start recording; click again to stop manually
 *  - Auto-stops after `silenceTimeoutMs` (default 4 s) of silence
 *  - While recording: pulsing red mic icon
 *  - On stop (auto or manual): calls onTranscript(text)
 *  - On cancel (no speech captured): calls onCancel()
 *  - onInterim(liveText) fires in real-time for textarea preview
 *
 * Props:
 *  - onTranscript(text) — final confirmed transcript
 *  - onCancel()         — recording stopped with no speech
 *  - onListeningChange(bool)
 *  - onVoiceActivity(bool) — true while interim speech is detected
 *  - onInterim(text)    — live partial transcript for preview
 *  - locale             — BCP 47 locale string
 *  - disabled
 *  - silenceTimeoutMs   — auto-stop delay (default 4000)
 */

import React, { useCallback, useEffect, useRef } from 'react';
import useVoiceInput from '../../hooks/useVoiceInput';

export default function VoiceInput({
  onTranscript,
  onCancel,
  onListeningChange,
  onVoiceActivity,
  onInterim,
  locale = 'en-US',
  disabled = false,
  silenceTimeoutMs = 4000,
}) {
  const lastTranscriptRef = useRef('');

  const handleResult = useCallback((text) => {
    lastTranscriptRef.current = text;
  }, []);

  const handleError = useCallback((err) => {
    console.warn('[VoiceInput] STT error:', err);
  }, []);

  const handleSilenceStop = useCallback((text) => {
    if (text?.trim()) {
      onTranscript?.(text.trim());
    } else {
      onCancel?.();
    }
    lastTranscriptRef.current = '';
  }, [onTranscript, onCancel]);

  const {
    isSupported,
    isListening,
    transcript,
    interim,
    error: voiceError,
    startListening,
    stopListening,
    resetTranscript,
    setLocale,
  } = useVoiceInput({
    locale,
    continuous: true,
    onResult: handleResult,
    onError: handleError,
    onSilenceStop: handleSilenceStop,
    silenceTimeoutMs,
  });

  useEffect(() => { setLocale(locale); }, [locale, setLocale]);
  useEffect(() => { onListeningChange?.(isListening); }, [isListening, onListeningChange]);
  useEffect(() => { onVoiceActivity?.(!!interim); }, [interim, onVoiceActivity]);

  // Stream live transcript to parent for textarea preview
  useEffect(() => {
    if (isListening) {
      const combined = transcript + (interim ? (transcript ? ' ' : '') + interim : '');
      onInterim?.(combined);
    }
  }, [transcript, interim, isListening, onInterim]);

  const handleMicClick = useCallback(() => {
    if (disabled) return;
    if (isListening) {
      // Manual stop — confirm whatever was captured
      stopListening();
      const text = (lastTranscriptRef.current || transcript).trim();
      if (text) {
        onTranscript?.(text);
      } else {
        onCancel?.();
      }
      resetTranscript();
      lastTranscriptRef.current = '';
    } else {
      resetTranscript();
      lastTranscriptRef.current = '';
      startListening();
    }
  }, [disabled, isListening, stopListening, transcript, onTranscript, onCancel, resetTranscript, startListening]);

  if (!isSupported) {
    return (
      <button
        type="button"
        disabled
        className="flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center
          text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-40"
        title="Voice input not supported in this browser"
      >
        <MicIcon className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="relative flex-shrink-0 flex items-center">
      <button
        type="button"
        onClick={handleMicClick}
        disabled={disabled}
        className={[
          'w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-200',
          isListening
            ? 'text-rose-500 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10'
            : 'text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-800',
          'disabled:opacity-40 disabled:cursor-not-allowed',
        ].join(' ')}
        title={isListening ? 'Stop recording' : 'Voice input (auto-stops after 4 s silence)'}
        aria-label={isListening ? 'Stop voice recording' : 'Start voice input'}
        aria-pressed={isListening}
      >
        {isListening ? (
          <span className="relative flex items-center justify-center w-4 h-4">
            <span className="absolute inset-0 rounded-full animate-ping bg-rose-400/40" />
            <MicIcon className="w-4 h-4 relative" />
          </span>
        ) : (
          <MicIcon className="w-4 h-4" />
        )}
      </button>

      {voiceError && (
        <div className="absolute bottom-full right-0 mb-1 bg-red-500 text-white text-[10px] px-2 py-1 rounded-md shadow-lg whitespace-nowrap z-20">
          Mic error
        </div>
      )}
    </div>
  );
}

// ── Mic Icon SVG ─────────────────────────────────────────────────────────────

function MicIcon({ className = '' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}
